/**
 * @fileoverview The box a note is written in: the text, then Cancel (left) and Save (right) in an ActionRow, and a
 * count of what is left once it gets close to the limit.
 *
 * One box for every place a note is written (a plant's journal, a whole bed),
 * so writing a note looks and works the same wherever it happens. The note
 * shortcuts go directly under it, placed by the caller.
 *
 * Where: NotesSection (a plant's or a bed's journal) and BedActions (one note
 * for the plants ticked in a bed).
 * API: none; the server's limits.NOTE_MAX_LENGTH (lib/growSettings) caps it.
 * Settings: NOTES_COUNTER_WITHIN (config/tunables/plantList): how close to
 * the limit the count appears.
 * B111: the foot is the shared ActionRow (Cancel outline on the left, Save filled and wider on the right, both 44px);
 * Save waits as Button loading (spinner and the Saving word from the Button's own map).
 * Words (en/es): plants.notes.placeholder, plants.notes.save, sitewide.cancel, sitewide.saving (Button loading).
 *
 * @module NoteBox
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { NOTES_COUNTER_WITHIN } from '@/config/tunables/plantList';

/** The note being written, and what Save and Cancel do with it. */
interface NoteBoxProps {
  /** The text in the box. */
  value: string;
  /** Given the new text, already cut to the note length limit. */
  onChange: (value: string) => void;
  /** Files the note. */
  onSave: () => void;
  /** Closes the box without filing anything. */
  onCancel: () => void;
  /** True while a save is on its way: Save shows its waiting state (Button loading) and Cancel waits. */
  busy?: boolean;
  /** Save cannot be pressed yet, for a reason the caller shows. */
  saveDisabled?: boolean;
}

/**
 * The textarea with Cancel and Save under it, and the count of what is left near
 * the limit. Forwards its ref to the textarea, so a caller can focus it as
 * the box opens.
 *
 * @param props - NoteBoxProps.
 * @param ref - The textarea.
 */
export const NoteBox = React.forwardRef<HTMLTextAreaElement, NoteBoxProps>(({ value, onChange, onSave, onCancel, busy = false, saveDisabled = false }, ref) => {
  const { reactTranslate: t } = useTranslation();
  const limits = useGrowLimits();
  // Only once it is close, so it reads as a warning not decoration.
  const max = limits.NOTE_MAX_LENGTH;
  const nearLimit = max !== undefined && value.length >= max - NOTES_COUNTER_WITHIN;
  return (
    <div className="space-y-2">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, max))}
        maxLength={max}
        rows={3}
        placeholder={t('plants.notes.placeholder')}
        className="w-full rounded-md border border-input bg-card p-2 text-sm outline-none focus:border-primary"
      />
      {nearLimit && (
        <div className={cn(
          'text-right text-xs tabular-nums',
          max !== undefined && value.length >= max ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {(max ?? 0) - value.length}
        </div>
      )}
      <ActionRow onCancel={onCancel} disabled={busy}>
        <Button size="lg" onClick={onSave} loading={busy} disabled={saveDisabled || value.trim().length === 0}>
          {t('plants.notes.save')}
        </Button>
      </ActionRow>
    </div>
  );
});
NoteBox.displayName = 'NoteBox';
